// pages/ContentList.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Box,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
} from '@mui/material';
import {
  Search,
  Sort,
  Visibility,
  Edit,
  Delete,
  Add,
  MoreVert,
  Article,
} from '@mui/icons-material';
import { db } from '../../lib/firebase';
import { collection, query, orderBy, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { deleteFileByUrl } from '../../lib/githubUpload';
import { motion } from 'framer-motion';

const ContentList = () => {
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedContent, setSelectedContent] = useState(null);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchContents();
  }, []);

  const fetchContents = async () => {
    try {
      const contentsRef = collection(db, 'contents');
      const q = query(contentsRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const contentsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      }));
      setContents(contentsData);
    } catch (err) {
      setError('Failed to load contents');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (content) => {
    if (window.confirm('Are you sure you want to delete this content?')) {
      try {
        // Extract and delete images from GitHub
        const imgRegex = /<img[^>]+src="([^">]+)"/g;
        let match;
        while ((match = imgRegex.exec(content.content)) !== null) {
          await deleteFileByUrl(match[1]);
        }

        // Delete from Firestore
        await deleteDoc(doc(db, 'contents', content.id));
        
        // Refresh list
        await fetchContents();
        alert('Content deleted successfully');
      } catch (err) {
        console.error('Error deleting content:', err);
        alert('Failed to delete content');
      }
    }
    handleMenuClose();
  };

  const handleMenuOpen = (event, content) => {
    setAnchorEl(event.currentTarget);
    setSelectedContent(content);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedContent(null);
  };

  const handleImportToBlog = (content) => {
    navigate('/admin/blogs/create', { 
      state: { 
        importedContent: {
          title: content.title,
          content: content.content,
          excerpt: content.excerpt,
          category: content.category,
          tags: content.tags,
        }
      } 
    });
  };

  const filteredContents = contents.filter(content =>
    content.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    content.content?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedContents = [...filteredContents].sort((a, b) => {
    if (sortBy === 'newest') return b.createdAt - a.createdAt;
    if (sortBy === 'oldest') return a.createdAt - b.createdAt;
    if (sortBy === 'title') return a.title.localeCompare(b.title);
    return 0;
  });

  // Skeleton Loader Component
  const ContentCardSkeleton = () => (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Skeleton variant="rounded" width={80} height={32} sx={{ borderRadius: '16px' }} />
          <Skeleton variant="circular" width={32} height={32} />
        </Box>
        <Skeleton variant="text" width="90%" height={32} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="100%" height={20} />
        <Skeleton variant="text" width="95%" height={20} />
        <Skeleton variant="text" width="80%" height={20} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="60%" height={20} />
      </CardContent>
      <CardActions sx={{ p: 2, pt: 0 }}>
        <Skeleton variant="rectangular" width={60} height={30} sx={{ borderRadius: '4px' }} />
        <Skeleton variant="rectangular" width={100} height={30} sx={{ borderRadius: '4px', ml: 1 }} />
        <Skeleton variant="rectangular" width={60} height={30} sx={{ borderRadius: '4px', ml: 1 }} />
      </CardActions>
    </Card>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Skeleton variant="text" width={200} height={48} />
          <Skeleton variant="rectangular" width={150} height={36} sx={{ borderRadius: '4px' }} />
        </Box>
        <Box display="flex" gap={2} mb={4}>
          <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: '4px' }} />
          <Skeleton variant="rectangular" width={120} height={56} sx={{ borderRadius: '4px' }} />
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {[1, 2, 3, 4, 5, 6].map((item) => (
            <Box key={item} sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33.333% - 16px)' } }}>
              <ContentCardSkeleton />
            </Box>
          ))}
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4} flexWrap="wrap" gap={2}>
          <Typography variant="h4" component="h1" fontWeight="bold" sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Content Library
          </Typography>
          {currentUser && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/admin/content/create')}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 5px 15px rgba(102, 126, 234, 0.4)',
                },
                transition: 'all 0.3s',
              }}
            >
              Create Content
            </Button>
          )}
        </Box>
      </motion.div>

      {/* Search and Filter Bar */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Box display="flex" gap={2} mb={4} flexWrap="wrap">
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search content..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            startIcon={<Sort />}
            onClick={() => setSortBy(sortBy === 'newest' ? 'oldest' : sortBy === 'oldest' ? 'title' : 'newest')}
            sx={{
              minWidth: 120,
              '&:hover': {
                transform: 'translateY(-2px)',
              },
              transition: 'transform 0.2s',
            }}
          >
            {sortBy === 'newest' ? 'Newest First' : sortBy === 'oldest' ? 'Oldest First' : 'By Title'}
          </Button>
        </Box>
      </motion.div>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Content Grid - Flexbox instead of Grid */}
      {sortedContents.length === 0 && !loading ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="text.secondary">
              No content found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Create your first content by clicking the "Create Content" button
            </Typography>
          </Box>
        </motion.div>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 3,
            justifyContent: { xs: 'center', sm: 'flex-start' },
          }}
        >
          {sortedContents.map((content, index) => (
            <motion.div
              key={content.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              style={{
                width: '100%',
                flex: '1 1 auto',
              }}
            >
              <Box
                sx={{
                  width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33.333% - 16px)' },
                  minWidth: { xs: '100%', sm: '280px' },
                }}
              >
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Chip
                        label={content.category || 'Draft'}
                        size="small"
                        color={content.status === 'published' ? 'success' : 'default'}
                        sx={{
                          fontWeight: 'bold',
                          '&:hover': {
                            transform: 'scale(1.05)',
                          },
                          transition: 'transform 0.2s',
                        }}
                      />
                      <IconButton 
                        size="small" 
                        onClick={(e) => handleMenuOpen(e, content)}
                        sx={{
                          '&:hover': {
                            transform: 'rotate(90deg)',
                          },
                          transition: 'transform 0.3s',
                        }}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography 
                      variant="h6" 
                      component="h2" 
                      gutterBottom 
                      fontWeight="bold"
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          color: '#667eea',
                        },
                        transition: 'color 0.2s',
                        minHeight: 64,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                      onClick={() => navigate(`/admin/content/${content.id}`)}
                    >
                      {content.title}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      paragraph
                      sx={{
                        minHeight: 80,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {content.excerpt || content.content?.substring(0, 150)}...
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {content.createdAt?.toLocaleDateString()} • By {content.author || 'Anonymous'}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0, gap: 1 }}>
                    <Button
                      size="small"
                      startIcon={<Visibility />}
                      onClick={() => navigate(`/admin/content/${content.id}`)}
                      sx={{
                        textTransform: 'none',
                        '&:hover': {
                          transform: 'translateX(4px)',
                        },
                        transition: 'transform 0.2s',
                      }}
                    >
                      View
                    </Button>
                    <Button
                      size="small"
                      startIcon={<Article />}
                      onClick={() => handleImportToBlog(content)}
                      sx={{
                        textTransform: 'none',
                        '&:hover': {
                          transform: 'translateX(4px)',
                        },
                        transition: 'transform 0.2s',
                      }}
                    >
                      Import to Blog
                    </Button>
                    {currentUser && currentUser.uid === content.userId && (
                      <Button
                        size="small"
                        startIcon={<Edit />}
                        onClick={() => navigate(`/admin/content/edit/${content.id}`)}
                        sx={{
                          textTransform: 'none',
                          '&:hover': {
                            transform: 'translateX(4px)',
                          },
                          transition: 'transform 0.2s',
                        }}
                      >
                        Edit
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Box>
            </motion.div>
          ))}
        </Box>
      )}

      {/* Menu */}
      <Menu 
        anchorEl={anchorEl} 
        open={Boolean(anchorEl)} 
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            '& .MuiMenuItem-root': {
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
            },
          },
        }}
      >
        <MenuItem onClick={() => navigate(`/admin/content/${selectedContent?.id}`)}>
          <Visibility sx={{ mr: 1 }} /> View
        </MenuItem>
        <MenuItem onClick={() => handleImportToBlog(selectedContent)}>
          <Article sx={{ mr: 1 }} /> Import to Blog
        </MenuItem>
        {currentUser && currentUser.uid === selectedContent?.userId && (
          <>
            <MenuItem onClick={() => navigate(`/admin/content/edit/${selectedContent?.id}`)}>
              <Edit sx={{ mr: 1 }} /> Edit
            </MenuItem>
            <MenuItem onClick={() => handleDelete(selectedContent)} sx={{ color: 'error.main' }}>
              <Delete sx={{ mr: 1 }} /> Delete
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Results Count */}
      {sortedContents.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Box textAlign="center" mt={4}>
            <Typography variant="body2" color="text.secondary">
              Showing {sortedContents.length} content{sortedContents.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </motion.div>
      )}
    </Container>
  );
};

export default ContentList;